import { Auth } from 'aws-amplify';
import { useEffect, useState } from 'react';
import { CognitoUserInterface } from '../../types/auth-types';

interface QRCode {
  qrSecret: string;
  qrCode: string;
}

interface UseOtpQrCodeResult {
  code: QRCode;
  loadingQrCode: boolean;
}

let totpSetupInProgress = false;
let totpSetupPromise: Promise<string> | null = null;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function hasTotpEnabled(freshUser: CognitoUserInterface) {
  const authTyped: typeof Auth = Auth;
  const options = authTyped.getMFAOptions
    ? await authTyped.getMFAOptions(freshUser)
    : null;
  if (Array.isArray(options)) {
    if ((options as unknown as string[]).includes('SOFTWARE_TOKEN_MFA'))
      return true;
  }

  const preferred = await authTyped.getPreferredMFA(freshUser);
  if (preferred === 'TOTP' || preferred === 'SOFTWARE_TOKEN_MFA') return true;

  return false;
}

export const useOtpQrCode = (
  user?: CognitoUserInterface,
): UseOtpQrCodeResult => {
  const [loadingQrCode, setLoading] = useState(false);
  const [code, setCode] = useState<QRCode>({ qrSecret: '', qrCode: '' });

  useEffect(() => {
    if (!user?.username) return;
    let isActive = true;

    (async () => {
      setLoading(true);
      try {
        const freshUser = await Auth.currentAuthenticatedUser({
          bypassCache: true,
        });

        if (await hasTotpEnabled(freshUser)) {
          if (!isActive) return;
          setCode({ qrSecret: '', qrCode: '' });
          return;
        }

        const attemptSetup = async (): Promise<string> => {
          try {
            return await Auth.setupTOTP(freshUser);
          } catch (err: unknown) {
            const errorObj = err as { name?: string; message?: string };
            const msg = String(errorObj?.message || '');
            const isConcurrent =
              errorObj?.name === 'ConcurrentModificationException' ||
              /Only one request to update user MFA settings/i.test(msg);
            if (isConcurrent) {
              await sleep(1200);
              return await Auth.setupTOTP(freshUser);
            }
            throw err;
          }
        };

        let qrSecret: string;
        if (totpSetupInProgress && totpSetupPromise) {
          qrSecret = await totpSetupPromise;
        } else {
          totpSetupInProgress = true;
          totpSetupPromise = attemptSetup();
          try {
            qrSecret = await totpSetupPromise;
          } finally {
            totpSetupInProgress = false;
            totpSetupPromise = null;
          }
        }

        if (!isActive) return;
        setCode({
          qrSecret,
          qrCode: `otpauth://totp/Corelight%20Investigator:${encodeURIComponent(
            freshUser.username,
          )}?secret=${qrSecret}&issuer=Corelight%20Investigator`,
        });
      } catch (e) {
        if (isActive) {
          console.error('[useOtpQrCode] Failed to prepare TOTP:', e);
        }
      } finally {
        if (isActive) setLoading(false);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [user?.username]);

  return { code, loadingQrCode };
};
